import { CareSendEncryptionError } from '@caresend/types';
import FileSaver from 'file-saver';
import { addToDecryptionBatch } from './batching.js';
import { getEncryptionCachedData, cacheEncryptionData } from './caching.js';
import { DownloadOption } from './model.js';
import { decryptBlob } from './utils/client.js';
import { fetchBlob, blob2file } from './utils/helpers.js';

/**
 * Decrypt any given data
 * @param data Encrypted data that needs to be decrypted
 * @return {Promise<any>}
 */
const decryptData = async (data, debugReference) => {
    // Check that data was already decrypted and stored in cache
    let decryptedData = getEncryptionCachedData(data.id);
    if (!decryptedData) {
        // Fetch decrypted data from cloud function
        const rawData = await addToDecryptionBatch(data);
        decryptedData = rawData.data;
        cacheEncryptionData(data.id, decryptedData);
    }
    if (Object.values(CareSendEncryptionError).includes(decryptedData)) {
        const debugReferenceSuffix = debugReference
            ? ` -- Reference: ${debugReference}`
            : '';
        const error = new Error(`CARESEND ENCRYPTION ERROR -- ${decryptedData}${debugReferenceSuffix}`);
        Promise.reject(error);
    }
    return decryptedData;
};
/**
 * Decrypt any file
 * @param encryptedFile EncryptedFile object to decrypt
 * @param castToURL boolean to resolve decrypted file as a URL for on-screen rendering
 * @return {Promise<File | string>} Decrypted file or URL
 */
const decryptFile = async (encryptedFile, castToURL) => {
    const { dataURL, key: encryptedKey, metadata } = encryptedFile;
    const decryptedKey = await decryptData(encryptedKey);
    const encryptedBlob = await fetchBlob(dataURL);
    const decryptedBlob = await decryptBlob(encryptedBlob, decryptedKey);
    const file = blob2file(decryptedBlob, metadata);
    // Must cast to conditional return types
    if (castToURL)
        return URL.createObjectURL(file);
    return file;
};
const unwrapMaybeEncryptedData = async (maybeEncryptedData, debugReference) => {
    const isEncrypted = maybeEncryptedData === null || maybeEncryptedData === void 0 ? void 0 : maybeEncryptedData.encrypted;
    if (isEncrypted) {
        return decryptData(maybeEncryptedData, debugReference);
    }
    return maybeEncryptedData;
};
const decryptAndDownloadFile = async (encryptedFile, downloadOption) => {
    const file = await decryptFile(encryptedFile, true);
    if (downloadOption === DownloadOption.OPEN)
        return window.open(file);
    FileSaver.saveAs(file, encryptedFile.metadata.fileName);
};

export { decryptAndDownloadFile, decryptData, decryptFile, unwrapMaybeEncryptedData };
